<template>
    <IndexModelLayout
        :createRoute="$route('admin::users.create')"
        :modelNamePlural="__('admin::users.users')"
    >

        <Table
            :headings="[
                __('admin::labels.name'),
                __('admin::labels.email'),
                __('admin::labels.phone'),
                __('admin::labels.company'),
                __('admin::labels.departments'),
                __('admin::labels.organization_level'),
                __('admin::labels.role'),
                {
                    heading: icons.gmail,
                    tooltip: __('admin::labels.table_label_gmail_sync'),
                },
                {
                    heading: icons.wordpress,
                    tooltip: __('admin::labels.table_label_wordpress_sync'),
                },
                {
                    heading: icons.forward,
                    tooltip: __('admin::labels.table_label_forward_email'),
                },
                {
                    heading: icons.autoreply,
                    tooltip: __('admin::labels.table_label_use_autoreplay'),
                },
                __('admin::labels.status'),
            ]"
            :paginator="paginator"
            @filter="submit"
        >
            <template #filters>
                <div class="grid grid-cols-8 gap-2">
                    <Input :placeholder="__('admin::forms.search')" v-model="filter.search" variant="filter" class="col-span-2" />
                    <MultipleSelect :options="companyOptions" v-model="filter.companies" :placeholder="__('admin::labels.all_companies')" @update:modelValue="submit" class="col-span-2" />
                    <MultipleSelect :options="departmentOptions" v-model="filter.departments" :placeholder="__('admin::labels.all_departments')" @update:modelValue="submit" class="col-span-2" />
                    <MultipleSelect :options="statusOptions" v-model="filter.statuses" :placeholder="__('admin::labels.all_statuses')" @update:modelValue="submit" class="col-span-2" />
                </div>
            </template>

            <template #buttons>
                <a
                    :href="$route('admin::exports.users')"
                    class="flex justify-center py-3 h-11 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 bg-gray-800 hover:bg-gray-900 focus:ring-gray-700"
                >
                    <DownloadIcon class="h-5 w-5 text-white" />
                </a>
            </template>

            <template #results>
                <tr v-for="employment in paginator.data" :key="employment.id">
                    <td class="whitespace-nowrap"><TableLink :href="$route('admin::users.general.edit', { user: employment.user.id })">{{ employment.user.name }}</TableLink></td>
                    <td class="whitespace-nowrap">{{ employment.email }}</td>
                    <td class="whitespace-nowrap">{{ employment.phone }}</td>
                    <td class="whitespace-nowrap">{{ employment.company.name }}</td>
                    <td>{{ (employment.departments || []).map((x) => departmentOptions[x]).join(', ') }}</td>
                    <td>{{ organizationLevelOptions[employment.organization_level] || '—' }}</td>
                    <td>{{ roleOptions[employment.user.role] || '—' }}</td>
                    <td>
                        <CheckIcon class="w-4 h-4 text-green-500" v-if="employment.sync_with_gmail" />
                        <XIcon class="w-4 h-4 text-red-500" v-else />
                    </td>
                    <td>
                        <CheckIcon class="w-4 h-4 text-green-500" v-if="employment.sync_with_wordpress" />
                        <XIcon class="w-4 h-4 text-red-500" v-else />
                    </td>
                    <td>
                        <CheckIcon class="w-4 h-4 text-green-500" v-if="employment.forward_email" />
                        <XIcon class="w-4 h-4 text-red-500" v-else />
                    </td>
                    <td>
                        <CheckIcon class="w-4 h-4 text-green-500" v-if="employment.use_autoreply" />
                        <XIcon class="w-4 h-4 text-red-500" v-else />
                    </td>
                    <td class="w-1 whitespace-nowrap">
                        <StatusBadge :status="employment.status" :name="statusOptions[employment.status] || 'unknown'" />
                    </td>
                    <td>
                        <TableButton
                            variant="edit"
                            :href="$route('admin::users.general.edit', { user: employment.user.id })"
                        />
                        <TableButton
                            variant="destroy"
                            @delete="deleting = $route('admin::users.destroy', { user: employment.user.id })"
                        />
                    </td>
                </tr>
            </template>

        </Table>

        <DeleteDialog
            :title="__('admin::general.delete_model', { model: __('admin::users.user') })"
            :message="__('admin::users.delete_user_not_only_employment')"
            :url="deleting"
            v-if="deleting"
            @closed="deleting = null"
        />

    </IndexModelLayout>
</template>

<script>
import {CheckIcon, DownloadIcon, XIcon} from "@heroicons/vue/solid";

    export default {
        components: {
            CheckIcon,
            XIcon,
            DownloadIcon,
        },
        props: {
            paginator: Object,
            request: Object,
            roleOptions: Object,
            organizationLevelOptions: Object,
            companyOptions: Object,
            departmentOptions: Object,
            statusOptions: Object,
        },
        data() {
            return {
                filter: this.$inertia.form({
                    search: this.request.search,
                    companies: this.request.companies || [],
                    departments: this.request.departments || [],
                    statuses: this.request.statuses || ['active', 'upcoming'],
                }),
                deleting: null,
                icons: {
                    gmail: '<svg class="w-auto h-5" height="48px" viewBox="0 0 48 48" width="48px"><path d="M45,16.2l-5,2.75l-5,4.75L35,40h7c1.657,0,3-1.343,3-3V16.2z" fill="#4caf50"/><path d="M3,16.2l3.614,1.71L13,23.7V40H6c-1.657,0-3-1.343-3-3V16.2z" fill="#1e88e5"/><polygon fill="#e53935" points="35,11.2 24,19.45 13,11.2 12,17 13,23.7 24,31.95 35,23.7 36,17"/><path d="M3,12.298V16.2l10,7.5V11.2L9.876,8.859C9.132,8.301,8.228,8,7.298,8h0C4.924,8,3,9.924,3,12.298z" fill="#c62828"/><path d="M45,12.298V16.2l-10,7.5V11.2l3.124-2.341C38.868,8.301,39.772,8,40.702,8h0 C43.076,8,45,9.924,45,12.298z" fill="#fbc02d"/></svg>',
                    wordpress: '<svg class="w-auto h-5" enable-background="new 0 0 512 512" id="Layer_1" version="1.1" viewBox="0 0 512 512" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g><path d="M254.9,507.9C117.1,507.9,5.9,395.3,6.5,255.2c0.6-138,112.1-250.5,251.4-249   c136.2,1.5,246.9,113.4,245.6,253.3C502.3,396.8,392.1,507.6,254.9,507.9z M254.7,488.1c124.2,0.5,227.8-100.5,229.2-228.4   c1.5-130.7-102.9-232.8-226.6-234C131.6,24.6,26.9,126.4,26.2,254.7C25.4,384.4,127.6,488.1,254.7,488.1z" fill="#218DBE"/><path d="M357.7,336.9c3-9.3,6.2-18.6,8.8-28c4.9-17.6,10.8-34.9,16.6-52.3c11.5-34.3,6.8-66.2-13.7-95.9   c-5.6-8.1-10.2-16.7-12.1-26.6c-3-15.8,4.8-31.3,18.8-37c14.4-5.9,31.9-0.8,40.8,12.1c6.9,10.1,9.3,21.6,10,33.5   c1.8,30.7-4.8,59.7-15.8,88.2c-9.5,24.8-17.5,50.3-26.2,75.4c-8.6,24.8-17.1,49.7-25.7,74.5c-2.9,8.5-6,16.9-8.8,25.4   c-1.4,4.2-3.6,5.6-8.2,5.9c-7.4,0.4-11-1.8-13.9-9c-8.5-21-15.7-42.4-23.5-63.6c-11-29.9-21.8-60-32.7-90c-0.4-1.2-0.6-2.9-2.3-2.7   c-1.7,0.1-1.6,1.9-2,3.1c-7.6,22-15.1,44-22.7,66c-10.7,30.9-21.6,61.9-32.3,92.8c-0.8,2.2-1.9,3.4-4.3,3.2c-0.3,0-0.7,0-1,0   c-13.1,0.4-12.9,0.4-17.3-11.8c-16.3-44.9-32.6-89.7-48.9-134.6c-13.2-36.3-26.3-72.5-39.5-108.8C98.9,149,99,149,90.6,149   c-2.5,0-5,0-7.5-0.2c-6-0.5-10-4.7-9.9-9.9c0.2-5.4,4.3-9,10.4-9.1c10.3-0.2,20.6-0.6,31-0.5c18.5,0,37-0.3,55.4-1.9   c1.5-0.1,3-0.2,4.5-0.2c4.3-0.1,7.2,1.8,7.7,6.1c0.5,4.3-1.7,7.2-5.9,7.9c-5.6,0.9-11.2,1.4-16.8,1.7c-3.8,0.2-4.5,1.1-3.2,4.8   c14.1,41.6,28,83.2,42,124.8c6.5,19.5,13,39.1,19.6,58.7c0.8,2.4,1.2,6.5,3.5,6.6c2.9,0.1,2.5-4.1,3.3-6.4   c11.9-35.5,23.7-71,35.6-106.4c0.8-2.5,1-4.8,0.1-7.3c-8.6-23.4-17.2-46.8-25.5-70.3c-1.2-3.3-2.7-4.4-6.1-4.4   c-4.3,0-8.6-0.6-12.9-1c-5.5-0.5-9-3.8-8.6-7.9c0.4-4.5,3.9-7.1,9.5-6.7c21.8,1.7,43.6,2.5,65.4,1.7c8.1-0.3,16.3-1,24.4-1.5   c1.2-0.1,2.3-0.2,3.5-0.2c5.1-0.1,8.1,2.1,8.6,6.3c0.5,4-2.2,7.5-7.1,8.1c-5.1,0.7-10.3,1.1-15.4,1.2c-3.8,0-4.9,0.7-3.6,4.8   c7.6,22.3,15,44.8,22.5,67.2c13,38.8,25.9,77.6,38.9,116.3c0.7,2,1.6,3.9,2.4,5.8C356.9,336.9,357.3,336.9,357.7,336.9z" fill="#218DBE"/></g></svg>',
                    forward: '<svg class="w-auto h-5" id="Icons" style="enable-background:new 0 0 32 32;" version="1.1" viewBox="0 0 32 32" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><path d="M23,15c-4.4,0-8,3.6-8,8s3.6,8,8,8s8-3.6,8-8S27.4,15,23,15z M26.7,23.7C26.7,23.7,26.7,23.7,26.7,23.7l-3.1,3  c-0.2,0.2-0.4,0.3-0.7,0.3c-0.3,0-0.5-0.1-0.7-0.3c-0.4-0.4-0.4-1,0-1.4l1.3-1.3H19c-0.6,0-1-0.4-1-1s0.4-1,1-1h4.5l-1.3-1.3  c-0.4-0.4-0.4-1,0-1.4c0.4-0.4,1-0.4,1.4,0l3.1,3c0,0,0,0,0,0c0.2,0.2,0.3,0.4,0.3,0.7S26.9,23.5,26.7,23.7z"/><path d="M13,23c0-1.4,0.3-2.8,0.9-4c-0.2,0-0.4-0.1-0.5-0.2l-1.6-1.4l-5.1,4.4C6.5,21.9,6.2,22,6,22c-0.3,0-0.6-0.1-0.8-0.3  c-0.4-0.4-0.3-1,0.1-1.4l4.9-4.2l-4.9-4.2c-0.4-0.4-0.5-1-0.1-1.4c0.4-0.4,1-0.5,1.4-0.1l7.3,6.4l7.3-6.4c0.4-0.4,1-0.3,1.4,0.1  c0.4,0.4,0.3,1-0.1,1.4L21,13.2c0.6-0.1,1.3-0.2,2-0.2c1.4,0,2.8,0.3,4,0.8V9c0-1.7-1.3-3-3-3H4C2.3,6,1,7.3,1,9v14c0,1.7,1.3,3,3,3  h9.5C13.2,25.1,13,24,13,23z"/></svg>',
                    autoreply: '<svg class="w-auto h-5" id="Icons" style="enable-background:new 0 0 32 32;" version="1.1" viewBox="0 0 32 32" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g><path d="M15.3,7.1V6c0-0.8-0.4-1.5-1.1-1.8c-0.6-0.3-1.4-0.2-2,0.3l-6.5,6C5.3,10.8,5,11.4,5,12s0.3,1.2,0.7,1.5l6.6,6   c0.6,0.4,1.3,0.5,1.9,0.2c0.7-0.3,1.1-1,1.1-1.8v-1c6.3,0.1,12.1,4.1,14.7,10.4c0.2,0.4,0.5,0.6,0.9,0.6c0.1,0,0.1,0,0.2,0   c0.5-0.1,0.8-0.5,0.8-1C32,16.7,24.8,8.2,15.3,7.1z"/><path d="M2,12.1c0,0,0-0.1,0-0.2l6.2-6.2c0.4-0.4,0.4-1,0-1.4c-0.4-0.4-1-0.4-1.4,0l-6.2,6.1C0.3,10.8,0,11.4,0,12s0.3,1.2,0.6,1.5   l6.2,6.2C7.1,19.9,7.3,20,7.6,20c0.3,0,0.5-0.1,0.7-0.3c0.4-0.4,0.4-1,0-1.4L2,12.1z"/></g></svg>',
                },
            }
        },
        methods: {
            submit() {
                this.filter.get(this.$route('admin::users.index'));
            },
        },
    }
</script>
